import { useContext, type Context } from "react";

import {
  ApiKeysContext,
  PoliciesContext,
  RolesContext,
  UsersContext,
  type ApiKeysData,
  type PoliciesData,
  type RolesData,
  type UsersData,
} from "../context/AccessDataContext";

/**
 * Reader hooks for the shared access data. Each returns the same shape as the underlying data hook
 * (`useRoles`, `usePolicies`, …) — `load` re-fetches that slice to invalidate it after a mutation. Must be
 * used under `<AccessDataProvider>`.
 */
function useAccessContext<T>(context: Context<T | null>, hook: string): T {
  const value = useContext(context);
  if (value === null) {
    throw new Error(`${hook} must be used within an <AccessDataProvider>`);
  }
  return value;
}

/** Shared roles catalog (`GET /internal/roles`). `load` re-fetches to invalidate after a mutation. */
export function useRolesData(): RolesData {
  return useAccessContext(RolesContext, "useRolesData");
}

/** Shared policies catalog (`GET /internal/policies`). `load` re-fetches to invalidate after a mutation. */
export function usePoliciesData(): PoliciesData {
  return useAccessContext(PoliciesContext, "usePoliciesData");
}

/** Shared users + their assigned roles, with auto-save mutators. */
export function useUsersData(): UsersData {
  return useAccessContext(UsersContext, "useUsersData");
}

/** Shared API keys + their assigned roles, with lifecycle and auto-save mutators. */
export function useApiKeysData(): ApiKeysData {
  return useAccessContext(ApiKeysContext, "useApiKeysData");
}
